import InputField from "components/common/controls/InputField";
import FormGroup from "components/common/FormGroup";
import SubscriptionTokenResponse from "interfaces/response/SubscriptionTokenResponse";
import { EditorMode } from "shared/enums/EditorModeEnum";
import Button from "components/common/controls/Button";
import { useRef, useEffect } from "react";
import { connect } from "react-redux";
import { ValidationGroup } from "devextreme-react/validation-group";
import Datepicker from "components/common/controls/Datepicker";
import {
  updateToken,
  createNewToken,
  getTokenTags,
} from "store/slices/TokenSlice";
import SubscriptionTokenRequest from "interfaces/request/SubscriptionTokenRequest";
import { unwrapResult } from "@reduxjs/toolkit";
import { RootState } from "store/store";
import TagControl from "components/common/TagControl";
import Checkbox from "components/common/controls/Checkbox";
import moment from "moment";
import { getSelectedProductId, getSelectedProductSubscriptionId } from "shared/utility/ProductSubscriptionHelper";

interface Props {
  mode?: EditorMode;
  token?: SubscriptionTokenResponse | null;
  selectedProductId?: number | null;
  availableTags?: string[];
  selectedProductLicenseId?: number | null;
  onCancel: () => void;
  onTokenCreateSuccess?: () => void;
  dispatch?: any;
}

export function TokenEditor(props: Props) {
  const {
    mode,
    token,
    selectedProductLicenseId,
    selectedProductId,
    availableTags,
    dispatch,
    onCancel,
    onTokenCreateSuccess,
  } = props;

  const isInEditMode = mode === EditorMode.Edit;
  const formValidationGroupRef = useRef<any>(null);
  const formRef = useRef<any>(null);
  const tagControlRef = useRef<any>(null);

  useEffect(() => {
    if (selectedProductId) {
      dispatch(getTokenTags(selectedProductId));
    }
  }, [dispatch, selectedProductId]);

  const handleSubmit = (e: any) => {
    var validationResult = formValidationGroupRef.current.instance.validate();
    if (!validationResult.isValid) {
      return;
    }
    const { name, isActive, expirationTime } = formRef.current;
    const requestTokenObject: SubscriptionTokenRequest = {
      id: isInEditMode ? token?.id : null,
      name: name.value,
      isActive: isActive?.value,
      expirationTime: expirationTime.value,
      tags: tagControlRef.current?.getSelectedTags() ?? [],
      subscriptionId: isInEditMode ? token?.subscriptionId : selectedProductLicenseId,
    };

    if (isInEditMode) {
      dispatch(updateToken(requestTokenObject))
        .then(unwrapResult)
        .then((response: SubscriptionTokenResponse) => {
          onCancel?.();
        });
    } else {
      dispatch(createNewToken(requestTokenObject))
        .then(unwrapResult)
        .then((response: SubscriptionTokenResponse) => {
          onTokenCreateSuccess?.();
        });
    }
  };

  const Footer = () => {
    return (
      <div className="float-right">
        <Button
          variant="secondary"
          text="Cancel"
          className="mr-2"
          onClick={onCancel}
        />
        <Button
          variant="primary"
          text={isInEditMode ? "SAVE CHANGES" : "Create New"}
          onClick={handleSubmit}
        />
      </div>
    );
  };

  return (
    <div>
      <form ref={formRef}>
        <ValidationGroup ref={formValidationGroupRef}>
          <div className="grid gap-y-6">
            <FormGroup label="Name (max 50 characters)">
              <InputField
                placeholder="Name"
                name="name"
                defaultValue={token?.name}
                maxLength={50}
                required
                id="name"
                label="Name"
              />
            </FormGroup>
            <FormGroup label="Expiration date">
              <Datepicker
                name="expirationTime"
                id="expirationTime"
                highlightedDates={
                  token?.expirationTime ? [moment(token?.expirationTime)] : []
                }
                label="Expiration date"
                placeholder="Expiration date"
                displayFormat="dd/MM/yyyy"
                defaultValue={
                  token?.expirationTime ? new Date(token?.expirationTime) : null
                }
                required
              />
            </FormGroup>
            {isInEditMode && (
              <FormGroup label="Status">
                <Checkbox
                  className="w-28"
                  name="isActive"
                  text="Active"
                  label="Active"
                  defaultValue={token?.isActive}
                />
              </FormGroup>
            )}
            <FormGroup label="Tags">
              <TagControl
                tags={availableTags ?? []}
                selectedTags={token?.tags ?? []}
                selectable={true}
                allowCreateNew={true}
                ref={tagControlRef}
              />
            </FormGroup>
            {isInEditMode && (
              <FormGroup label="Key">
                <span>
                  For security reasons, we do not store token keys on our web
                  servers. Keys are only presented to you once during key
                  generation. Lost your token key? Regenerate the token in the
                  token management screen.
                </span>
              </FormGroup>
            )}
          </div>
          <div className="mt-12">
            <Footer />
          </div>
        </ValidationGroup>
      </form>
    </div>
  );
}

TokenEditor.defaultProps = {
  mode: EditorMode.Create,
};

const mapStateToProps = (state: RootState, props: Props) => {
  const selectedProductId = getSelectedProductId(state);
  const availableTags = selectedProductId
    ? state.tokenData.tokenTags[selectedProductId] || []
    : [];

  return {
    ...props,
    selectedProductId,
    availableTags,
    selectedProductLicenseId: getSelectedProductSubscriptionId(state),
  };
};

export default connect(mapStateToProps)(TokenEditor);
